import { WIND_ATTRIBUTION_URL, pickLocale } from "@divespots/contracts";
import type { SpotListItem } from "@divespots/contracts";
import type { Map as LeafletMap } from "leaflet";
import { TriangleAlert } from "lucide-react";
import type { CSSProperties } from "react";
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useLakes, usePrecipField, useSpot, useSpots, useWindField } from "../api/index.ts";
import { Button } from "../components/Button.tsx";
import { GlassSelect } from "../components/GlassSelect.tsx";
import { AdminLink } from "../admin/AdminLink.tsx";
import { AccountMenu } from "../components/AccountMenu.tsx";
import { TopMenu } from "../components/TopMenu.tsx";
import { LanguageSelect } from "../components/LanguageSelect.tsx";
import { SearchField } from "../components/SearchField.tsx";
import { SegmentedControl } from "../components/SegmentedControl.tsx";
import { detentHeights, useViewportHeight } from "../components/Sheet.tsx";
import type { Detent } from "../components/Sheet.tsx";
import { useSpotViewEvent } from "../analytics-hooks.ts";
import { useLocale } from "../i18n/index.ts";
import { MapControls } from "../map/MapControls.tsx";
import { SavedSpotsButton, SavedSpotsPanel } from "../offline/SavedSpotsPanel.tsx";
import { OverviewMap } from "../map/OverviewMap.tsx";
import { MapScale } from "../map/MapScale.tsx";
import type { BaseLayer } from "../map/tiles.ts";
import { searchSpots } from "../search.ts";
import { SpotSheet } from "../spot/SpotSheet.tsx";
import { useOnline } from "../pwa/online.ts";
import { usePrecipPref } from "../map/precip/storage.ts";
import { readPrecip } from "../map/precip/readout.ts";
import { useWindPref } from "../map/wind/storage.ts";
import { readWind } from "../map/wind/readout.ts";
import { useReducedMotion } from "../map/wind/useReducedMotion.ts";
import { useWindTime } from "../map/wind/useWindTime.ts";
import {
  BOTH_BAR_RESERVE_PX,
  WIND_BAR_RESERVE_PX,
  WIND_CHIP_RESERVE_PX,
  WindTimeBar,
} from "../map/wind/WindTimeBar.tsx";

// three.js lives in its own chunk, loaded only when a 3D view is opened.
const ThreeDView = lazy(() => import("../spot/three/ThreeDView.tsx"));
import { useMediaQuery } from "../useMediaQuery.ts";

const PANEL_W = 380;
/** The slim strip the spot panel collapses to in 3D on desktop. */
const STRIP_W = 56;
const PANEL_KEY = "divespots.panel3d";

/** Remembered for the session only. */
function readCollapsed(): boolean {
  try {
    return sessionStorage.getItem(PANEL_KEY) === "collapsed";
  } catch {
    return false;
  }
}

const NO_IDS: ReadonlySet<string> = new Set();

/** `/:lang/`, `/:lang/spot/:slug` and `/:lang/spot/:slug/3d`: the map is always there; sheet and 3D view are route-driven. */
export function MapPage({ slug, view3d = false }: { slug: string | undefined; view3d?: boolean }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const spotsQuery = useSpots();
  const lakesQuery = useLakes();
  useSpotViewEvent(slug, view3d ? "3d" : "2d");
  const selectedDetail = useSpot(slug);

  const [layer, setLayer] = useState<BaseLayer>("aerial");
  const online = useOnline();
  const [windPref, setWindPref] = useWindPref();
  // the stored choice survives going offline; the overlay itself needs the network
  const windOn = windPref && online;
  const windQuery = useWindField(windOn);
  const [precipPref, setPrecipPref] = usePrecipPref();
  const precipOn = precipPref && online;
  const precipQuery = usePrecipField(precipOn);
  const windReady = windOn && !!windQuery.data;
  const precipReady = precipOn && !!precipQuery.data;
  // the bar speaks for wind unless only precipitation has arrived (wind failed or is still loading)
  const barWind = windOn && (windReady || !precipReady);
  const anyOn = windOn || precipOn;
  // wind is on but failed while precipitation shows: the bar must say so
  const windFailed = windOn && !windReady && precipReady && windQuery.isError;
  const windTime = useWindTime({
    windOn: windReady,
    wind: windQuery.data,
    precipOn,
    precip: precipQuery.data,
  });
  const reducedMotion = useReducedMotion();
  const [lakeSlug, setLakeSlug] = useState("");
  const [query, setQuery] = useState("");
  const [map, setMap] = useState<LeafletMap | null>(null);
  const [detent, setDetent] = useState<Detent>("medium");
  const [panelCollapsed, setPanelCollapsedState] = useState(readCollapsed);
  const setPanelCollapsed = useCallback((c: boolean) => {
    setPanelCollapsedState(c);
    try {
      sessionStorage.setItem(PANEL_KEY, c ? "collapsed" : "open");
    } catch {
      /* private mode: the choice just is not remembered */
    }
  }, []);
  const [savedOpen, setSavedOpen] = useState(false);
  const [outsideIds, setOutsideIds] = useState<ReadonlySet<string>>(NO_IDS);
  const vh = useViewportHeight();
  const desktop = useMediaQuery("(min-width: 900px)");
  // landscape phones can be 900 px wide: the scale bar wants a real desktop window
  const showScale = useMediaQuery("(min-width: 900px) and (min-height: 500px)");
  // phones: search plus one menu button on the top row, the rest lives in the menu and the control column
  const compact = useMediaQuery("(max-width: 599.98px)");

  // 3D only exists for spots with bathymetry; a /3d deep link on any other spot falls back to 2D.
  const show3d = view3d && selectedDetail.data?.hasBathy === true;
  useEffect(() => {
    if (view3d && selectedDetail.data && !selectedDetail.data.hasBathy) {
      void navigate(`/${locale}/spot/${slug}`, { replace: true });
    }
  }, [view3d, selectedDetail.data, navigate, locale, slug]);
  // On phones the 3D canvas takes the map area while the sheet stays at peek.
  useEffect(() => setDetent(show3d && !desktop ? "peek" : "medium"), [slug, show3d, desktop]);

  const selectedName = selectedDetail.data?.name;
  useEffect(() => {
    document.title = slug && selectedName ? `${selectedName} · ${t("app.name")}` : t("app.srTitle");
  }, [slug, selectedName, t]);

  const lakes = lakesQuery.data;
  const visibleSpots = useMemo(
    () => (spotsQuery.data ?? []).filter((s) => !lakeSlug || s.lakeSlug === lakeSlug),
    [spotsQuery.data, lakeSlug],
  );
  const results = useMemo(
    () => searchSpots(visibleSpots, query, locale, lakes ?? []),
    [visibleSpots, query, locale, lakes],
  );

  const selectedLocation =
    spotsQuery.data?.find((s) => s.slug === slug)?.location ??
    selectedDetail.data?.location ??
    undefined;
  const [mapCenter, setMapCenter] = useState<{ lat: number; lon: number } | null>(null);
  useEffect(() => {
    if (!map || !anyOn) return;
    const read = () => {
      const c = map.getCenter();
      setMapCenter({ lat: c.lat, lon: c.lng });
    };
    read();
    map.on("moveend", read);
    return () => void map.off("moveend", read);
  }, [map, anyOn]);
  const readoutPlace = useMemo(() => {
    const at = slug && selectedLocation ? selectedLocation : mapCenter;
    return at
      ? { at, name: slug && selectedLocation ? (selectedName ?? slug) : t("wind.placeCenter") }
      : null;
  }, [slug, selectedLocation, mapCenter, selectedName, t]);
  const windReadout = useMemo(() => {
    const field = windQuery.data;
    if (!windReady || !field || windTime.t === null || !readoutPlace) return null;
    const { at, name } = readoutPlace;
    return { place: name, wind: readWind(field, at.lat, at.lon, windTime.t) };
  }, [windReady, windQuery.data, windTime.t, readoutPlace]);
  const precipReadout = useMemo(() => {
    const field = precipQuery.data;
    if (!precipOn || !field || windTime.t === null || !readoutPlace) return null;
    const { at, name } = readoutPlace;
    return { place: name, reading: readPrecip(field, at.lat, at.lon, windTime.t) };
  }, [precipOn, precipQuery.data, windTime.t, readoutPlace]);
  const sheetPx = detentHeights(vh).medium;
  // with the wind bar up, the pin lands between the top bar and the bar, not under it
  // (landscape phones with a spot open show a one-line chip instead of the bar)
  const shortLandscape = useMediaQuery("(max-height: 450px)");
  const chipOnly = !!slug && shortLandscape;
  const barPx =
    anyOn && !show3d && !desktop
      ? chipOnly
        ? WIND_CHIP_RESERVE_PX
        : (barWind && precipOn) || windFailed
          ? BOTH_BAR_RESERVE_PX
          : WIND_BAR_RESERVE_PX
      : 0;
  // a short landscape view also has a two-row top bar to clear
  const topClearPx = !desktop && !compact && shortLandscape ? 120 : barPx ? 64 : 0;
  const flyOffset = useMemo(
    () =>
      desktop ? { x: -(PANEL_W + 16) / 2, y: 0 } : { x: 0, y: (sheetPx + barPx - topClearPx) / 2 },
    [desktop, sheetPx, barPx, topClearPx],
  );

  const goToSpot = useCallback(
    (s: string) => void navigate(`/${locale}/spot/${s}`),
    [navigate, locale],
  );
  const setView = useCallback(
    (v: "2d" | "3d") => void navigate(`/${locale}/spot/${slug}${v === "3d" ? "/3d" : ""}`),
    [navigate, locale, slug],
  );
  const to2d = useCallback(() => setView("2d"), [setView]);
  const close = useCallback(() => void navigate(`/${locale}/`), [navigate, locale]);

  const pinLabel = useCallback(
    (spot: SpotListItem, closed: boolean) =>
      t(
        spot.permanentClosure
          ? "map.spotPinPermanent"
          : closed
            ? "map.spotPinClosed"
            : "map.spotPin",
        { name: spot.name },
      ),
    [t],
  );
  const clusterLabel = useCallback((count: number) => t("map.cluster", { count }), [t]);

  const lakeOptions = [
    { value: "", label: t("lakeFilter.all") },
    ...(lakes ?? []).map((l) => ({ value: l.slug, label: pickLocale(l.name, locale) ?? l.slug })),
  ];

  // On phones the large sheet covers the floating credit's spot: show it inside the sheet instead.
  const showFloatingCredit = desktop || !slug || detent !== "large";

  // Desktop 3D: the panel can fold into a slim strip so the lakebed gets the screen.
  const collapsedStrip = show3d && desktop && panelCollapsed;

  const shellStyle = {
    "--sheet-visible": slug && !desktop ? `${detentHeights(vh)[detent]}px` : "0px",
    "--panel-w": `${collapsedStrip ? STRIP_W : PANEL_W}px`,
  } as CSSProperties;

  return (
    <div
      className="app-shell"
      data-sheet-open={slug ? "true" : "false"}
      data-detent={slug && !desktop ? detent : undefined}
      style={shellStyle}
    >
      <OverviewMap
        spots={visibleSpots}
        layer={layer}
        selectedSlug={slug}
        selectedLocation={selectedLocation}
        flyOffset={flyOffset}
        onSelect={goToSpot}
        onMapReady={setMap}
        pinLabel={pinLabel}
        clusterLabel={clusterLabel}
        ariaLabel={t("map.label")}
        wind={
          !show3d && windOn && windQuery.data && windTime.t !== null
            ? { field: windQuery.data, t: windTime.t }
            : undefined
        }
        precip={
          !show3d && precipOn && precipQuery.data && windTime.t !== null
            ? { field: precipQuery.data, t: windTime.t }
            : undefined
        }
      />

      {show3d && selectedDetail.data ? (
        <Suspense
          fallback={<div className="three-root" role="status" aria-label={t("three.loading")} />}
        >
          <ThreeDView
            spot={selectedDetail.data}
            onBack={to2d}
            onOutside={setOutsideIds}
            reservedLeft={desktop ? (collapsedStrip ? STRIP_W + 16 : PANEL_W + 16) : 0}
          />
        </Suspense>
      ) : null}

      <div className="status-scrim" aria-hidden="true" />
      <header className="topbar" data-hidden={show3d} data-compact={compact}>
        <h1 className="sr-only">{t("app.srTitle")}</h1>
        <div className="topbar__row">
          <div className="topbar__search">
            <SearchField
              value={query}
              onChange={setQuery}
              results={results.map((s) => ({
                id: s.slug,
                title: s.name,
                subtitle: [s.locality, s.waterBody].filter(Boolean).join(" · "),
              }))}
              onSelect={(id) => {
                setQuery("");
                goToSpot(id);
              }}
              label={t("search.label")}
              placeholder={t("search.placeholder")}
              clearLabel={t("search.clear")}
              noResultsText={t("search.noResults")}
              resultsCountText={t("search.results", { count: results.length })}
            />
          </div>
          {compact ? (
            <TopMenu
              lakeSlug={lakeSlug}
              onLakeChange={setLakeSlug}
              lakeOptions={lakeOptions}
              onOpenSaved={() => setSavedOpen(true)}
            />
          ) : (
            <div className="topbar__lang flex items-center gap-2">
              <SavedSpotsButton onClick={() => setSavedOpen(true)} />
              <AdminLink />
              <AccountMenu />
              <LanguageSelect />
            </div>
          )}
        </div>
        {compact ? null : (
          <div className="topbar__row">
            <div className="topbar__lake">
              <GlassSelect
                value={lakeSlug}
                onChange={setLakeSlug}
                options={lakeOptions}
                label={t("lakeFilter.label")}
              />
            </div>
            <div className="topbar__layer">
              <SegmentedControl
                glass
                aria-label={t("map.layerLabel")}
                value={layer}
                onChange={setLayer}
                options={[
                  { value: "map", label: t("map.layerMap") },
                  { value: "aerial", label: t("map.layerAerial") },
                ]}
              />
            </div>
          </div>
        )}
      </header>

      {show3d ? null : (
        <MapControls
          map={map}
          aerial={
            compact
              ? {
                  on: layer === "aerial",
                  onToggle: () => setLayer(layer === "aerial" ? "map" : "aerial"),
                }
              : undefined
          }
          wind={{ on: windOn, onToggle: () => setWindPref(!windPref), disabled: !online }}
          precip={{
            on: precipOn,
            onToggle: () => setPrecipPref(!precipPref),
            disabled: !online,
          }}
        />
      )}

      {anyOn && !show3d ? (
        <WindTimeBar
          time={windTime}
          status={
            (barWind ? windQuery : precipQuery).data
              ? "ready"
              : (barWind ? windQuery : precipQuery).isError
                ? "unavailable"
                : "loading"
          }
          locale={locale}
          runAt={(barWind ? windQuery : precipQuery).data?.runAt}
          windOn={barWind}
          windFailed={windFailed}
          mode={reducedMotion ? "arrows" : "particles"}
          readout={windReadout}
          precip={
            precipOn
              ? {
                  status: precipQuery.data
                    ? "ready"
                    : precipQuery.isError
                      ? "unavailable"
                      : "loading",
                  readout: precipReadout,
                }
              : undefined
          }
        />
      ) : null}

      {showScale && !show3d ? <MapScale map={map} /> : null}

      {showFloatingCredit ? (
        <footer className="map-attribution ds-glass ds-text-caption-1">
          {t("map.attribution")}
          {anyOn && !show3d ? (
            <>
              {" · "}
              {t(
                windOn && precipOn
                  ? "precip.creditBoth"
                  : precipOn
                    ? "precip.creditShort"
                    : "wind.creditShort",
              )}{" "}
              <span className="wind-credit-full" data-long={precipOn}>
                {t("wind.creditMeteo")}{" "}
              </span>
              <a href={WIND_ATTRIBUTION_URL} target="_blank" rel="noopener noreferrer">
                Open-Meteo
              </a>
            </>
          ) : null}
        </footer>
      ) : null}

      {spotsQuery.isError ? (
        <div className="map-toast ds-glass ds-text-subheadline" role="alert">
          <TriangleAlert
            aria-hidden="true"
            size={20}
            strokeWidth={1.75}
            className="text-warning shrink-0"
          />
          <span className="flex-1">{t("map.loadError")}</span>
          <Button variant="plain" onClick={() => void spotsQuery.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      ) : null}

      {savedOpen ? (
        <SavedSpotsPanel
          onClose={() => setSavedOpen(false)}
          onOpen={(s) => {
            setSavedOpen(false);
            goToSpot(s);
          }}
        />
      ) : null}

      {slug ? (
        <SpotSheet
          slug={slug}
          detent={desktop ? "medium" : detent}
          showCredit={!showFloatingCredit}
          outsideIds={show3d ? outsideIds : NO_IDS}
          onDetentChange={setDetent}
          view={show3d ? "3d" : "2d"}
          onViewChange={setView}
          panel={
            show3d && desktop
              ? { collapsed: panelCollapsed, onToggle: () => setPanelCollapsed(!panelCollapsed) }
              : undefined
          }
          onClose={close}
        />
      ) : null}
    </div>
  );
}
